package data.repository

import domain.repository.AuthenticationRepository
import data.storage.SecureStorage
import data.storage.StorageKeys
import data.datasource.remote.PlatformApi
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow

/**
 * Implementation of AuthenticationRepository
 * Manages authentication state and token persistence
 */
class AuthenticationRepositoryImpl(
    private val secureStorage: SecureStorage,
    private val platformApi: PlatformApi
) : AuthenticationRepository {
    
    private val _authenticationState = MutableStateFlow(false)
    
    init {
        // Initialize authentication state on creation
        checkInitialAuthenticationState()
        // Note: OAuth cookie checking is handled at the app lifecycle level
    }
    
    override fun observeAuthenticationState(): Flow<Boolean> {
        return _authenticationState.asStateFlow()
    }
    
    override suspend fun getCurrentToken(): String? {
        return try {
            println("🔍 AuthRepo: Checking for GitHub token...")
            
            // First check secure storage
            val storedToken = secureStorage.getDecrypted(StorageKeys.GITHUB_TOKEN)
            if (storedToken != null) {
                println("🔑 AuthRepo: Found stored token")
                updateAuthenticationState(true)
                return storedToken
            }
            
            // If no stored token, check cookie (for fresh OAuth returns)
            val cookieToken = platformApi.getCookie("github_token")
            if (cookieToken != null) {
                println("🔑 AuthRepo: Found token in cookie, storing it...")
                // Store it securely for future use
                secureStorage.storeEncrypted(StorageKeys.GITHUB_TOKEN, cookieToken)
                updateAuthenticationState(true)
                return cookieToken
            }
            
            println("❌ AuthRepo: No token found in storage or cookies")
            updateAuthenticationState(false)
            null
        } catch (e: Exception) {
            println("💥 AuthRepo: Error getting token: ${e.message}")
            updateAuthenticationState(false)
            null
        }
    }
    
    override fun initiateLogin() {
        println("🚀 AuthRepo: Initiating login...")
        platformApi.redirectTo("/api/oauth/github/login")
    }
    
    override suspend fun logout() {
        try {
            println("👋 AuthRepo: Logging out...")
            // Clear stored token
            secureStorage.remove(StorageKeys.GITHUB_TOKEN)
            secureStorage.remove(StorageKeys.USER_DATA)
            // Clear any remaining cookies
            platformApi.clearCookie("github_token")
            updateAuthenticationState(false)
            println("✅ AuthRepo: Logout completed - token and cookies cleared")
        } catch (e: Exception) {
            println("💥 AuthRepo: Error during logout: ${e.message}")
            // Still update state even if storage fails
            updateAuthenticationState(false)
        }
    }
    
    override suspend fun handleInvalidToken() {
        println("🚫 AuthRepo: Token is invalid, logging out...")
        logout()
    }
    
    override suspend fun processOAuthCallback() {
        try {
            println("🔍 AuthRepo: Checking for OAuth callback cookie...")
            
            // Check for GitHub token cookie
            val cookieToken = platformApi.getCookie("github_token")
            if (cookieToken != null) {
                println("✅ AuthRepo: Found OAuth callback cookie, storing it...")
                
                // Store the token securely
                secureStorage.storeEncrypted(StorageKeys.GITHUB_TOKEN, cookieToken)
                
                // Update auth state reactively (but keep the cookie for frontend use)
                updateAuthenticationState(true)
                
                println("✅ AuthRepo: OAuth token stored and auth state updated")
            } else {
                println("❌ AuthRepo: No OAuth callback cookie found")
            }
            
        } catch (e: Exception) {
            println("💥 AuthRepo: Error processing OAuth callback: ${e.message}")
        }
    }
    
    /**
     * Manually refresh authentication state - useful after login
     */
    suspend fun refreshAuthenticationState() {
        println("🔄 AuthRepo: Manually refreshing authentication state...")
        val token = getCurrentToken()
        val isAuthenticated = token != null
        updateAuthenticationState(isAuthenticated)
        println("🔄 AuthRepo: Refreshed auth state: $isAuthenticated")
    }
    
    private fun checkInitialAuthenticationState() {
        try {
            println("🔄 AuthRepo: Checking initial authentication state...")
            
            // Check if we have a stored token without the overhead of cookie checking
            val hasStoredToken = secureStorage.getPlain("encrypted_${StorageKeys.GITHUB_TOKEN}") != null
            println("🔍 AuthRepo: Stored token check result: $hasStoredToken")
            
            // Also check for plain token storage (fallback)
            val hasPlainToken = secureStorage.getPlain(StorageKeys.GITHUB_TOKEN) != null
            println("🔍 AuthRepo: Plain token check result: $hasPlainToken")
            
            val isAuthenticated = hasStoredToken || hasPlainToken
            updateAuthenticationState(isAuthenticated)
            println("🔄 AuthRepo: Initial auth state set to: $isAuthenticated")
            
        } catch (e: Exception) {
            println("💥 AuthRepo: Error checking initial auth state: ${e.message}")
            updateAuthenticationState(false)
        }
    }
    
    private fun updateAuthenticationState(isAuthenticated: Boolean) {
        if (_authenticationState.value != isAuthenticated) {
            _authenticationState.tryEmit(isAuthenticated)
            println("🔄 AuthRepo: Authentication state updated to: $isAuthenticated")
        }
    }

    /**
     * Debug method to log current authentication state
     */
    suspend fun debugAuthenticationState() {
        println("🔍 AuthRepo: === AUTHENTICATION DEBUG ===")
        
        try {
            // Check stored tokens
            val encryptedToken = secureStorage.getPlain("encrypted_${StorageKeys.GITHUB_TOKEN}")
            val plainToken = secureStorage.getPlain(StorageKeys.GITHUB_TOKEN)
            
            println("🔍 AuthRepo: Encrypted token exists: ${encryptedToken != null}")
            println("🔍 AuthRepo: Plain token exists: ${plainToken != null}")
            
            // Check cookie
            val cookieToken = platformApi.getCookie("github_token")
            println("🔍 AuthRepo: Cookie token exists: ${cookieToken != null}")
            
            // Check current authentication state
            val currentToken = getCurrentToken()
            println("🔍 AuthRepo: getCurrentToken() result: ${currentToken != null}")
            println("🔍 AuthRepo: Current auth state: ${_authenticationState.value}")
            
        } catch (e: Exception) {
            println("💥 AuthRepo: Error during debug: ${e.message}")
        }
        
        println("🔍 AuthRepo: === DEBUG END ===")
    }
}
